/**
 * LA TAILLE DU TEXTE — dans le jeu, pas dans le navigateur (design §5).
 *
 * POURQUOI PAS LE ZOOM DU NAVIGATEUR. Il agrandit tout, y compris la tranche, les tirettes et les
 * marges : au troisième cran, la partition à trois zones ne tient plus dans l'écran et les choix
 * passent sous la ligne de flottaison. Ici on n'agrandit QUE ce qui se lit — le corps du texte et
 * les mots de l'interface —, et la reliure garde ses proportions.
 *
 * CINQ CRANS, ET UNE ÉCHELLE PAR CRAN. Le nombre part dans `data-taille` sur la racine du document ;
 * la feuille de style en tire `--echelle-texte`, et tout le reste suit — le corps, les petits pas
 * typographiques, et la mesure de la colonne, qui est exprimée en `ch` et grandit donc AVEC les
 * lettres. C'est ce que le §5 demande : soixante-cinq caractères par ligne, quelle que soit la
 * taille.
 *
 * LE DÉCOUPAGE EN FEUILLETS SUIT AUSSI, et c'est le seul endroit où il faut y penser :
 * `mesureDeFeuillet` compte des caractères par écran, et un écran contient moins de caractères
 * quand ils sont plus gros. Sans cela, le cran « immense » donnerait des feuillets de trois écrans
 * — c'est-à-dire exactement le mur de texte qu'on a passé le lot 4 à défaire.
 *
 * Le choix s'écrit dans le navigateur du joueur et se relit AVANT la première peinture (six lignes
 * dans `index.html`, comme le thème) : un joueur qui a choisi le très grand ne doit pas prendre un
 * éclair de petit texte à chaque chargement.
 */
import { useCallback, useSyncExternalStore } from "react";

export const CRANS = [
  { clef: "serre", nom: "Serré", echelle: 0.9 },
  { clef: "normal", nom: "Normal", echelle: 1 },
  { clef: "grand", nom: "Grand", echelle: 1.15 },
  { clef: "tres-grand", nom: "Très grand", echelle: 1.32 },
  { clef: "immense", nom: "Immense", echelle: 1.5 }
] as const;

export type Cran = (typeof CRANS)[number]["clef"];

export const CRAN_PAR_DEFAUT: Cran = "normal";

const CLEF = "destinia.taille";
const CHANGEMENT = "destinia:taille";

/** Un cran connu, ou celui par défaut. Une valeur inconnue dans `localStorage` — une vieille clef,
 * un doigt sur le clavier — ne doit pas rendre le texte illisible ni faire tomber l'écran. */
export function cranValide(valeur: string | null | undefined): Cran {
  return (CRANS.find((c) => c.clef === valeur)?.clef ?? CRAN_PAR_DEFAUT) as Cran;
}

export function echelleDe(cran: Cran): number {
  return CRANS.find((c) => c.clef === cran)?.echelle ?? 1;
}

/** Le cran voisin, en restant dans les bornes. C'est ce que font les deux boutons « A− » et « A+ » :
 * un réglage à cinq crans se règle en tâtonnant, pas en choisissant dans une liste. */
export function cranVoisin(cran: Cran, sens: 1 | -1): Cran {
  const rang = CRANS.findIndex((c) => c.clef === cran);
  const suivant = Math.min(CRANS.length - 1, Math.max(0, rang + sens));
  return CRANS[suivant]!.clef;
}

function actuel(): Cran {
  return cranValide(document.documentElement.dataset.taille);
}

export function poseLaTaille(cran: Cran): void {
  document.documentElement.dataset.taille = cran;
  try {
    localStorage.setItem(CLEF, cran);
  } catch {
    /* stockage refusé : la taille vivra le temps de la session, et c'est tout */
  }
  window.dispatchEvent(new Event(CHANGEMENT));
}

export function useTailleDuTexte(): [Cran, (cran: Cran) => void] {
  const cran = useSyncExternalStore(
    (rappel) => {
      window.addEventListener(CHANGEMENT, rappel);
      return () => window.removeEventListener(CHANGEMENT, rappel);
    },
    actuel,
    () => CRAN_PAR_DEFAUT
  );
  return [cran, useCallback((suivant: Cran) => poseLaTaille(suivant), [])];
}
